import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Mail, ShieldCheck } from "lucide-react";
import { ASSETS } from "@/lib/assets";

const UPDATED = "3 décembre 2026";

export default function ChildSafety() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Sécurité des enfants — MamaMatch";
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute("content") : null;
    if (meta) {
      meta.setAttribute(
        "content",
        "Politique de MamaMatch pour la protection des enfants et la lutte contre l'exploitation des mineurs (CSAE). Tolérance zéro, modération, signalement."
      );
    }
    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    if (canonical) {
      canonical.setAttribute(
        "href",
        "https://mamamatch.fr/securite-des-enfants"
      );
    }
    return () => {
      document.title = prevTitle;
      if (meta && prevDesc !== null) meta.setAttribute("content", prevDesc);
      if (canonical && prevCanonical !== null)
        canonical.setAttribute("href", prevCanonical);
    };
  }, []);
  return (
    <main
      data-testid="child-safety-page"
      className="min-h-screen bg-white text-[#0A0A0A]"
    >
      {/* Header */}
      <header className="border-b border-[rgba(10,10,10,0.06)]">
        <div className="mx-auto max-w-[1000px] px-5 md:px-8 h-20 md:h-24 flex items-center justify-between">
          <Link
            to="/"
            data-testid="cs-nav-logo"
            className="flex items-center -ml-4"
            aria-label="Retour à l'accueil"
          >
            <img
              src={ASSETS.logo}
              alt="MamaMatch"
              className="h-24 md:h-32 w-auto object-contain"
            />
          </Link>
          <Link
            to="/"
            data-testid="cs-back-link"
            className="btn-ghost inline-flex items-center gap-2 px-4 py-2.5 text-[13px] font-semibold"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à l'accueil
          </Link>
        </div>
      </header>

      <article className="mx-auto max-w-[760px] px-5 md:px-8 py-16 md:py-24">
        <div className="flex items-center gap-2 text-[12.5px] font-semibold uppercase tracking-[0.14em] text-[#D97757]">
          <ShieldCheck className="w-4 h-4" strokeWidth={1.8} />
          Politique publique
        </div>

        <h1
          data-testid="cs-title"
          className="mt-4 font-display text-[36px] sm:text-[46px] md:text-[56px] leading-[1.02] tracking-[-0.03em]"
        >
          Sécurité des enfants et protection des mineurs
        </h1>

        <p className="mt-4 text-[13px] text-[#6B6660]">
          Dernière mise à jour : {UPDATED}
        </p>

        <p className="mt-8 text-[16px] md:text-[17px] leading-[1.7] text-[#3a3835]">
          MamaMatch est une plateforme dédiée aux mamans qui souhaitent
          rencontrer d'autres mamans vivant la même étape de maternité. Bien
          que notre application soit exclusivement destinée à un public adulte,
          nous prenons très au sérieux la protection des enfants et des
          mineurs. Cette page décrit nos engagements et nos mesures concrètes,
          conformément aux exigences de Google Play concernant les
          applications sociales.
        </p>

        <Section title="1. Tolérance zéro concernant l'exploitation des enfants">
          MamaMatch applique une <strong>politique de tolérance zéro</strong>{" "}
          concernant toute forme d'exploitation, d'abus, ou de contenu
          pédocriminel (Child Sexual Abuse Material, CSAM). Tout contenu,
          comportement ou compte lié de près ou de loin à l'exploitation
          sexuelle d'enfants ou de mineurs (CSAE — Child Sexual Abuse and
          Exploitation) entraîne{" "}
          <strong>
            une suspension immédiate et définitive du compte, sans préavis
          </strong>
          , ainsi qu'un signalement aux autorités compétentes.
        </Section>

        <Section title="2. Âge minimum d'utilisation">
          L'accès à MamaMatch est strictement réservé aux personnes majeures
          (18 ans révolus). Toute inscription détectée comme émanant d'un
          mineur entraîne la suppression immédiate du compte. Nous nous
          réservons le droit de demander une vérification d'âge à tout moment.
        </Section>

        <Section title="3. Prévention et modération">
          <ul className="mt-4 space-y-3 list-disc pl-5">
            <li>
              <strong>Modération humaine :</strong> nos équipes examinent les
              profils, les signalements, et les échanges pouvant présenter un
              risque.
            </li>
            <li>
              <strong>Modération automatisée :</strong> des filtres analysent
              en continu les contenus (texte, images, photos de profil) pour
              détecter tout élément suspect ou inapproprié, notamment tout
              contenu pouvant mettre en danger un enfant.
            </li>
            <li>
              <strong>Vérification d'identité :</strong> nous mettons en place
              des mesures d'authentification pour limiter les faux comptes et
              les usurpations d'identité.
            </li>
            <li>
              <strong>Encadrement des photos :</strong> les photos publiées sur
              les profils sont vérifiées. Toute photo mettant en scène un
              mineur de façon inappropriée est immédiatement retirée et le
              compte concerné est sanctionné.
            </li>
          </ul>
        </Section>

        <Section title="4. Mécanisme de signalement">
          Chaque utilisatrice peut{" "}
          <strong>signaler un profil, un message ou un contenu</strong>{" "}
          directement depuis l'application, en un seul geste, sans avoir à
          quitter la conversation. Les signalements sont examinés par notre
          équipe de modération dans les meilleurs délais.
          <br />
          <br />
          Si tu souhaites signaler un contenu, un compte ou un comportement lié
          à l'exploitation ou à la mise en danger d'un enfant — que ce soit
          sur MamaMatch ou ailleurs — tu peux nous écrire à tout moment :
          <a
            href="mailto:mamamatchoff@gmail.com?subject=Signalement%20%E2%80%94%20S%C3%A9curit%C3%A9%20des%20enfants"
            data-testid="cs-report-email"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] text-white px-5 py-3 text-[14px] font-semibold hover:bg-[#1c1c1c] transition-colors"
          >
            <Mail className="w-4 h-4" />
            mamamatchoff@gmail.com
          </a>
        </Section>

        <Section title="5. Conformité aux lois nationales et internationales">
          MamaMatch se conforme aux lois françaises et européennes en matière
          de protection des mineurs, notamment&nbsp;:
          <ul className="mt-4 space-y-2 list-disc pl-5">
            <li>Le Règlement Général sur la Protection des Données (RGPD)</li>
            <li>
              La législation française relative à la protection de l'enfance
            </li>
            <li>
              Le Digital Services Act (DSA) de l'Union européenne concernant la
              modération des contenus illicites
            </li>
          </ul>
          En cas de détection d'un contenu ou d'un comportement illégal
          impliquant un mineur, MamaMatch coopère pleinement avec les
          autorités compétentes, notamment le Point de contact national
          français{" "}
          <a
            href="https://www.internet-signalement.gouv.fr"
            target="_blank"
            rel="noopener noreferrer"
            className="mm-underline"
          >
            Pharos
          </a>{" "}
          et l'
          <a
            href="https://www.internetwatch.org.uk"
            target="_blank"
            rel="noopener noreferrer"
            className="mm-underline"
          >
            Internet Watch Foundation (IWF)
          </a>
          .
        </Section>

        <Section title="6. Sensibilisation et prévention">
          Nous travaillons à sensibiliser notre communauté à la protection des
          enfants, à travers des messages de prévention, des rappels dans
          l'application, et un accès facile à des ressources d'aide reconnues
          (119 – Allô Enfance en Danger, e-Enfance, etc.).
        </Section>

        <Section title="7. Point de contact CSAE">
          Pour toute question, signalement ou demande liée à la sécurité des
          enfants ou à la lutte contre l'exploitation sexuelle des mineurs,
          notre point de contact dédié est&nbsp;:
          <div className="mt-4 rounded-2xl border border-[rgba(10,10,10,0.1)] bg-[#F7F5F2] p-5">
            <div className="text-[13px] font-semibold uppercase tracking-[0.14em] text-[#0A0A0A]/60">
              Contact dédié
            </div>
            <div className="mt-2 text-[16px] font-semibold text-[#0A0A0A]">
              L'équipe MamaMatch
            </div>
            <a
              href="mailto:mamamatchoff@gmail.com?subject=CSAE%20%E2%80%94%20Signalement"
              className="mt-1 inline-block text-[15px] mm-underline"
            >
              mamamatchoff@gmail.com
            </a>
            <p className="mt-3 text-[13.5px] text-[#6B6660] leading-relaxed">
              Nous nous engageons à traiter tout signalement lié à la sécurité
              d'un enfant dans les meilleurs délais et à coopérer avec les
              autorités compétentes.
            </p>
          </div>
        </Section>

        <Section title="8. Numéros d'urgence et ressources d'aide">
          <ul className="mt-4 space-y-2 list-disc pl-5">
            <li>
              <strong>119 — Allô Enfance en Danger</strong> (gratuit, 24h/24)
            </li>
            <li>
              <strong>17 — Police-Secours</strong> (urgences)
            </li>
            <li>
              <strong>3018 — e-Enfance</strong> (violences numériques envers les
              mineurs)
            </li>
            <li>
              <strong>Pharos</strong> —{" "}
              <a
                href="https://www.internet-signalement.gouv.fr"
                target="_blank"
                rel="noopener noreferrer"
                className="mm-underline"
              >
                internet-signalement.gouv.fr
              </a>
            </li>
          </ul>
        </Section>

        <p className="mt-16 pt-8 border-t border-[rgba(10,10,10,0.08)] text-[13.5px] text-[#6B6660] leading-relaxed">
          Cette politique peut évoluer pour refléter nos engagements et rester
          conforme à la réglementation. La date de dernière mise à jour est
          indiquée en haut de cette page.
        </p>

        <div className="mt-10 flex flex-wrap items-center gap-4">
          <Link
            to="/"
            data-testid="cs-footer-back"
            className="btn-primary inline-flex items-center gap-2 px-5 py-3 text-[14px] font-semibold"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à MamaMatch
          </Link>
          <a
            href="mailto:mamamatchoff@gmail.com"
            className="btn-ghost inline-flex items-center gap-2 px-5 py-3 text-[14px] font-semibold"
          >
            <Mail className="w-4 h-4" />
            Nous contacter
          </a>
        </div>
      </article>

      {/* Footer mini */}
      <footer className="border-t border-[rgba(10,10,10,0.08)] py-8">
        <div className="mx-auto max-w-[1000px] px-5 md:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[13px] text-[#6B6660]">
          <span>
            © {new Date().getFullYear()} MamaMatch. Fait avec soin en France.
          </span>
          <div className="flex items-center gap-6">
            <Link to="/" className="mm-underline">
              Accueil
            </Link>
            <a
              href="mailto:mamamatchoff@gmail.com"
              className="mm-underline"
            >
              mamamatchoff@gmail.com
            </a>
          </div>
        </div>
      </footer>
    </main>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-12 md:mt-14">
      <h2 className="font-display text-[22px] md:text-[26px] leading-[1.2] text-[#0A0A0A] tracking-[-0.02em]">
        {title}
      </h2>
      <div className="mt-4 text-[15px] md:text-[16px] leading-[1.7] text-[#3a3835]">
        {children}
      </div>
    </section>
  );
}
